import React, { useState, useEffect } from 'react';
import { Clock, Mail, Bell, ArrowRight, Rocket, Loader2, Crown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './button';
import { Input } from './input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './card';
import { useAuth } from '../../contexts/AuthContext';
import waitlistService from '../../services/waitlistService';

interface ComingSoonOverlayProps {
  title: string;
  description: string;
  features?: string[];
  expectedDate?: string;
  onWaitlistJoin?: (email: string) => Promise<void>;
  className?: string;
}

export const ComingSoonOverlay: React.FC<ComingSoonOverlayProps> = ({
  title,
  description,
  features = [],
  expectedDate,
  onWaitlistJoin,
  className = ''
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState(user?.email || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isOnWaitlist, setIsOnWaitlist] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkWaitlistStatus = async () => {
      if (user?.email) {
        try {
          const status = await waitlistService.getWaitlistStatus(user.email);
          if (status) {
            setIsOnWaitlist(true);
          }
        } catch (error) {
          // Not on waitlist, do nothing
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    checkWaitlistStatus();
  }, [user]);

  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onWaitlistJoin?.(email);
      setIsSubmitted(true);
      setEmail('');
    } catch (error) {
      console.error('Failed to join waitlist:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center p-8">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      );
    }

    if (isOnWaitlist || isSubmitted) {
      return (
        <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800/30 rounded-xl sm:rounded-2xl p-3 sm:p-4 text-center">
          <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-2">
            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-green-800 dark:text-green-300 font-medium text-sm sm:text-base">You're on the list!</p>
          <p className="text-green-600 dark:text-green-400 text-xs sm:text-sm mt-1">We'll notify you as soon as it's ready.</p>
        </div>
      );
    }

    // Show coming soon message
    return (
      <div className="space-y-2 sm:space-y-3">
        <p className="text-xs sm:text-sm text-center text-gray-600 dark:text-gray-400">
          This feature is currently in development
        </p>
        <Button 
          disabled
          className="w-full text-white shadow-lg transition-all duration-200 rounded-xl sm:rounded-2xl text-sm sm:text-base py-2 sm:py-3 cursor-not-allowed opacity-75"
          style={{ 
            background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 50%, #30b3dd 100%)',
            backgroundSize: '200% 200%',
            backgroundPosition: 'left center',
            boxShadow: '0 4px 15px rgba(48, 179, 221, 0.3)'
          }}
        >
          <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
          Coming Soon
        </Button>
      </div>
    );

    return null;
  };

  return (
    <div className={`${
      className?.includes('dashboard-analytics') 
        ? 'absolute inset-0 bg-black/10 backdrop-blur-sm z-50 flex items-center justify-center p-4' 
        : 'fixed inset-0 bg-black/10 backdrop-blur-sm z-50 flex items-center justify-center p-4 lg:pl-8'
    }`}>
      <div className={`${
        className?.includes('dashboard-analytics') 
          ? '' 
          : 'w-full flex justify-center lg:ml-16'
      }`}>
        <Card className="w-full max-w-xs sm:max-w-md mx-auto shadow-2xl border-2 border-blue-100 dark:border-blue-800/50 bg-white dark:bg-slate-900/95 backdrop-blur-sm rounded-2xl sm:rounded-3xl">
        <CardHeader className="text-center pb-3 sm:pb-4 px-4 sm:px-6">
          <div className="mx-auto w-10 h-10 sm:w-14 sm:h-14 rounded-full flex items-center justify-center mb-3 sm:mb-4 shadow-lg" style={{ background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 50%, #30b3dd 100%)', boxShadow: '0 4px 15px rgba(48, 179, 221, 0.3)' }}>
            <Rocket className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
          </div>
          <CardTitle className="text-lg sm:text-2xl font-bold text-black dark:text-white mb-2 sm:mb-3">
            {title}
          </CardTitle>
          <CardDescription className="text-black dark:text-gray-300 text-sm sm:text-base font-medium">
            {description}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-3 sm:space-y-5 px-4 sm:px-6">
          {features.length > 0 && (
            <div className="space-y-2 sm:space-y-3">
              <h4 className="font-semibold text-black dark:text-white flex items-center gap-2 text-base sm:text-lg">
                <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                What's Coming:
              </h4>
              <ul className="space-y-1.5 sm:space-y-2">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2 sm:gap-3 text-sm sm:text-base text-gray-600 dark:text-gray-300">
                    <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 text-blue-500 flex-shrink-0 mt-0.5 sm:mt-1" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {expectedDate && (
            <div className="bg-blue-50 dark:bg-blue-900/30 rounded-xl sm:rounded-2xl p-3 sm:p-4 flex items-center gap-2 sm:gap-3 border border-blue-100 dark:border-blue-800/30">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400" />
              <span className="text-sm sm:text-base text-blue-800 dark:text-blue-300">
                Expected: <strong>{expectedDate}</strong>
              </span>
            </div>
          )}

          {renderContent()}
        </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ComingSoonOverlay;
